import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { GiveawayCountdownTimer } from './GiveawayCountdownTimer';

const ABN_LOOKUP_URL = 'https://abr.business.gov.au/ABN/View?id=20630834762';

type Props = {
  drawDate: string | Date; // ✅ pass ISO string or Date
  prizeLabel?: string;
  prizeUnitLabel?: string;
  winnersSection?: React.ReactNode;
  totalsSection?: React.ReactNode;
  activeJackpotParticipantCount?: number;
};

const RaffleHero = ({
  drawDate,
  prizeLabel = 'A$10,000',
  prizeUnitLabel = 'INC Token',
  winnersSection,
  totalsSection,
  activeJackpotParticipantCount
}: Props) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const timerRef = useRef<HTMLDivElement | null>(null);
  const [showStickyTimer, setShowStickyTimer] = useState(false);

  const tickItems = useMemo(
    () => [
      'Fully Compliant Giveaway',
      'Proudly Australian Owned and Operated',
      'Winners Publicly Announced',
      'Digital Collectibles Included',
      'Fair & Transparent'
    ],
    []
  );

  const hasParticipantCount =
    typeof activeJackpotParticipantCount === 'number' &&
    Number.isFinite(activeJackpotParticipantCount);
  const participantCountLabel = (
    activeJackpotParticipantCount ?? 0
  ).toLocaleString('en-AU');

  useEffect(() => {
    if (!isMobileOrTablet) {
      setShowStickyTimer(false);
      return;
    }

    const updateStickyTimer = () => {
      const timer = timerRef.current;
      if (!timer) return;

      const rect = timer.getBoundingClientRect();
      setShowStickyTimer(rect.top <= 0);
    };

    updateStickyTimer();
    window.addEventListener('scroll', updateStickyTimer, { passive: true });
    window.addEventListener('resize', updateStickyTimer);

    return () => {
      window.removeEventListener('scroll', updateStickyTimer);
      window.removeEventListener('resize', updateStickyTimer);
    };
  }, [isMobileOrTablet]);

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: { xs: 4, md: 0 },
        background: '#ffffff'
      }}
    >
      {showStickyTimer && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1250,
            display: { xs: 'flex', lg: 'none' },
            justifyContent: 'center',
            px: 1,
            pt: 'calc(env(safe-area-inset-top) + 8px)',
            pb: 1,
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 78%, rgba(255,255,255,0) 100%)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <GiveawayCountdownTimer
              nextGiveaway={drawDate as Date}
              isTiny={true}
            />
            <Typography
              sx={{
                mt: 0.75,
                color: '#0f172a',
                fontWeight: 800,
                fontSize: { xs: 13, sm: 14 },
                textTransform: 'uppercase'
              }}
            >
              Till next {prizeLabel} draw, dont miss out!
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          py: { xs: 3, sm: 4.5, md: 5.5, lg: 2 },
          px: { xs: 2.5, sm: 4, md: 6 },
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 1150, textAlign: 'center' }}>
          <Box
            ref={timerRef}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <GiveawayCountdownTimer
                nextGiveaway={drawDate as Date}
                isTiny={isMobileOrTablet}
                isJumbo={!isMobileOrTablet}
              />
              <Typography
                sx={{
                  mt: 1,
                  color: '#0f172a',
                  fontWeight: 900,
                  fontSize: { xs: 13, sm: 16, md: 18, lg: 30 },
                  lineHeight: 1.08,
                  textTransform: 'uppercase'
                }}
              >
                Till next {prizeLabel} draw, dont miss out!
              </Typography>
              {hasParticipantCount && (
                <Box
                  sx={{
                    mt: { xs: 4, md: 3, lg: 4 },
                    display: 'inline-grid',
                    gridTemplateColumns: 'auto auto',
                    alignItems: 'center',
                    columnGap: { xs: 1.1, sm: 1.35, lg: 1.6 },
                    px: { xs: 1.5, sm: 1.9, lg: 2.6 },
                    py: { xs: 1, sm: 1.15, lg: 1.35 },
                    borderTop: '1px solid rgba(8,145,178,0.2)',
                    borderBottom: '1px solid rgba(8,145,178,0.2)'
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: '#0891b2',
                      fontWeight: 1000,
                      fontSize: { xs: 32, sm: 36, md: 46, lg: 60 },
                      lineHeight: 1,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {participantCountLabel}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: '#64748b',
                      fontWeight: 900,
                      fontSize: { xs: 13, sm: 14.5, md: 17, lg: 24 },
                      lineHeight: 1.1
                    }}
                  >
                    Users entered in the upcoming A$10k giveaway
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Title */}
          <Typography
            sx={{
              mt: { xs: 4.25, sm: 5, md: 5.25 },
              fontWeight: 900,
              letterSpacing: '-0.04em',
              color: '#0f172a',
              lineHeight: 1.05,
              fontSize: { xs: 42, sm: 58, md: 74, lg: 84 }
            }}
          >
            Win {prizeLabel} in {prizeUnitLabel}!
          </Typography>

          {winnersSection && (
            <Box sx={{ mt: { xs: 3, md: 4 } }}>{winnersSection}</Box>
          )}

          <Typography
            sx={{
              mt: { xs: 2.5, sm: 3, md: 3.2 },
              color: '#334155',
              fontWeight: 600,
              lineHeight: 1.55,
              fontSize: { xs: 18, sm: 20, md: 24 },
              maxWidth: 900,
              mx: 'auto'
            }}
          >
            Australia&apos;s most trusted weekly INC Token giveaway. Every entry
            comes with exclusive Digital Collectible rewards, so your next big win starts here.
          </Typography>

          <Box
            sx={{
              mt: 2.5,
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 1.2
            }}
          >
            {[
              {
                label: 'Permit Number: TP/03891',
                href: undefined
              },
              {
                label: 'ABN: 20630834762',
                href: ABN_LOOKUP_URL
              }
            ].map((meta) => (
              <Box
                key={meta.label}
                {...(meta.href
                  ? {
                      component: 'a',
                      href: meta.href,
                      target: '_blank',
                      rel: 'noopener noreferrer'
                    }
                  : {})}
                sx={{
                  px: { xs: 1.5, sm: 1.8 },
                  py: 0.8,
                  borderRadius: 999,
                  border: '1px solid rgba(36,191,189,0.18)',
                  background: 'rgba(36,191,189,0.08)',
                  color: '#0f172a',
                  fontSize: { xs: 13, sm: 14 },
                  fontWeight: 800,
                  letterSpacing: '0.01em',
                  textDecoration: 'none',
                  cursor: meta.href ? 'pointer' : 'default',
                  transition: 'transform 160ms ease, background 160ms ease',
                  ...(meta.href
                    ? {
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          background: 'rgba(36,191,189,0.14)'
                        }
                      }
                    : {})
                }}
              >
                {meta.label}
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: { xs: 3, md: 4 },
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(5, minmax(0, 1fr))'
              },
              gap: { xs: 1.2, sm: 1.4, md: 1.6 }
            }}
          >
            {tickItems.map((label) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                  justifyContent: 'center',
                  px: { xs: 1.4, sm: 1.6 },
                  py: 1.2,
                  borderRadius: 3,
                  border: '1px solid rgba(15,23,42,0.08)',
                  background: '#ffffff',
                  boxShadow: '0 10px 26px rgba(15,23,42,0.05)'
                }}
              >
                <CheckRoundedIcon sx={{ fontSize: 20, color: '#0891b2' }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: '#0f172a',
                    fontSize: { xs: 14, md: 15 },
                    lineHeight: 1.35
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          {totalsSection}
        </Box>
      </Box>
    </Box>
  );
};

export default RaffleHero;
